import React, { useEffect, useMemo, useState } from 'react';

import { produce } from 'immer';

import { ProfileErrorMessage, ProfileErrorMessages } from 'pages/Settings/GeoConfigSettings';
import {
  PostGeoConfig,
  PostGeoConfigLocation,
} from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import { GeoConfigContextInterface } from 'pages/Settings/GeoConfigSettings/utils/GeoConfigContext';

interface UseGeoConfigProps {
  dealerId?: number;
}

export const useGeoConfig = ({ dealerId }: UseGeoConfigProps): GeoConfigContextInterface => {
  // Selected config profile
  const [selectProfile, setSelectProfile] = useState<PostGeoConfig>();
  // Selected pin in the selected config profile
  const [selectPin, setSelectPin] = useState<PostGeoConfigLocation>();
  // Is the selected pin editable
  const [editingPin, setEditingPin] = useState<PostGeoConfigLocation>();
  // toggle create new profile state
  const [creatingNewProfile, setCreatingNewProfile] = useState<boolean>(false);
  // toggle modify existing profile state
  const [modifyingExistingProfile, setModifyingExistingProfile] = useState<boolean>(false);
  // All config profiles including unsaved profile
  const [tempGeoConfigs, setTempGeoConfigs] = useState<PostGeoConfig[]>([]);
  // Initial geo config, use for reset geo config
  const [initialGeoConfig, setInitialGeoConfig] = useState<PostGeoConfig[]>([]);
  // Last time saved geo config, use for move backward geo config
  const [lastSavedTempGeoConfig, setLastSavedTempGeoConfig] = useState<PostGeoConfig[]>([]);
  // Error message for the profile validation
  const [errorMessage, setErrorMessage] = useState<ProfileErrorMessage>({});

  const geoConfigProfileNames = useMemo(
    () => tempGeoConfigs.map((profile) => profile.name),
    [tempGeoConfigs],
  );

  const selectProfileIndex = useMemo(
    () => tempGeoConfigs.findIndex((geoConfig) => geoConfig.configId === selectProfile?.configId),
    [selectProfile, tempGeoConfigs],
  );

  const selectPinIndex = useMemo(
    () =>
      selectProfile?.locations.findIndex(
        (location) => location.id === selectPin?.id && location.name === selectPin?.name,
      ),
    [selectPin, selectProfile],
  );

  const editingPinIndex = useMemo(
    () =>
      selectProfile?.locations.findIndex(
        (location) => location.id === editingPin?.id && location.name === editingPin?.name,
      ),
    [editingPin, selectProfile],
  );

  useEffect(() => {
    setCreatingNewProfile(false);
    setSelectPin(undefined);
    setEditingPin(undefined);
  }, [dealerId]);

  useEffect(() => {
    if (editingPin) {
      const { latitude, longitude, radius } = editingPin;
      setErrorMessage(
        produce(errorMessage, (draft) => {
          if (latitude > 90 || latitude < -90) {
            draft.pinLatitude = ProfileErrorMessages.pinLatitude;
          } else {
            delete draft.pinLatitude;
          }
          if (longitude > 180 || longitude < -180) {
            draft.pinLongitude = ProfileErrorMessages.pinLongitude;
          } else {
            delete draft.pinLongitude;
          }
          if (radius > 500 || radius < 15 || !Number.isInteger(radius)) {
            draft.pinRadius = ProfileErrorMessages.pinRadius;
          } else {
            delete draft.pinRadius;
          }
          return draft;
        }),
      );
    } else {
      setErrorMessage({});
    }
  }, [editingPin]);

  useEffect(() => {
    if (selectProfile) {
      setSelectProfile(
        produce(selectProfile, (draft) => {
          draft = tempGeoConfigs[selectProfileIndex];
          return draft;
        }),
      );
    }
  }, [tempGeoConfigs, selectProfile, selectProfileIndex]);

  useEffect(() => {
    // update selectPin and editingPin to match the newest selectProfile
    if (selectPin && selectProfile && selectPinIndex !== undefined && selectPinIndex >= 0) {
      setSelectPin(selectProfile.locations[selectPinIndex]);
    }
    if (editingPin && selectProfile && editingPinIndex !== undefined && editingPinIndex >= 0) {
      setEditingPin(selectProfile.locations[editingPinIndex]);
    }
  }, [selectProfile, selectPinIndex, selectPin, editingPinIndex, editingPin]);

  const updateTempGeoConfigPin = (location: PostGeoConfigLocation) => {
    const { latitude, longitude, radius } = location;
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        if (selectPinIndex !== undefined && selectPinIndex >= 0) {
          draft[selectProfileIndex].locations[selectPinIndex].latitude = latitude;
          draft[selectProfileIndex].locations[selectPinIndex].longitude = longitude;
          draft[selectProfileIndex].locations[selectPinIndex].radius = radius;
        }

        return draft;
      }),
    );
  };

  const onLongChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        if (selectPinIndex !== undefined) {
          draft[selectProfileIndex].locations[selectPinIndex].longitude =
            Number(e.target.value) ?? 0;
        }
        return draft;
      }),
    );
  };

  const onRadiusChange = (value: number[]) => {
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        if (selectPinIndex !== undefined) {
          draft[selectProfileIndex].locations[selectPinIndex].radius = value[0];
        }
        return draft;
      }),
    );
  };

  const onLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        if (selectPinIndex !== undefined) {
          draft[selectProfileIndex].locations[selectPinIndex].latitude =
            Number(e.target.value) ?? 0;
        }
        return draft;
      }),
    );
  };

  return {
    selectProfile,
    setSelectProfile,
    selectPin,
    setSelectPin,
    editingPin,
    setEditingPin,
    creatingNewProfile,
    setCreatingNewProfile,
    modifyingExistingProfile,
    setModifyingExistingProfile,
    tempGeoConfigs,
    setTempGeoConfigs,
    initialGeoConfig,
    setInitialGeoConfig,
    lastSavedTempGeoConfig,
    setLastSavedTempGeoConfig,
    errorMessage,
    setErrorMessage,
    geoConfigProfileNames,
    selectProfileIndex,
    selectPinIndex,
    editingPinIndex,
    updateTempGeoConfigPin,
    onLongChange,
    onRadiusChange,
    onLatChange,
  };
};
